// @ts-nocheck
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Eye, EyeOff } from 'lucide-react';

const coachSchema = z.object({
  name: z.string().min(2, 'Name is required'),
  email: z.string().email('Valid email required'),
  username: z.string().min(3, 'Username must be at least 3 characters'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  phone: z.string().optional(),
  coachType: z.enum(['club', 'college']),
  organizationName: z.string().min(2, 'Organization name is required'),
});

type CoachFormData = z.infer<typeof coachSchema>;

interface MobileCoachRegistrationProps {
  onSuccess: () => void;
  onBack: () => void;
}

const MobileCoachRegistration: React.FC<MobileCoachRegistrationProps> = ({ 
  onSuccess,
  onBack 
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm<CoachFormData>({
    resolver: zodResolver(coachSchema)
  });

  const coachType = watch('coachType');

  const onSubmit = async (data: CoachFormData) => {
    setIsLoading(true);
    
    try {
      // Check if username is taken
      const { data: existingProfile } = await supabase
        .from('profiles')
        .select('username')
        .eq('username', data.username.toLowerCase())
        .single();

      if (existingProfile) {
        toast.error('Username is already taken');
        setIsLoading(false);
        return;
      }

      const roleSlug = data.coachType === 'club' ? 'club_coach' : 'college_coach';

      // Create auth user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: {
            name: data.name,
            username: data.username,
            role: roleSlug
          },
          emailRedirectTo: `${window.location.origin}/`
        }
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error('Failed to create account');

      // Wait for profile trigger
      await new Promise(resolve => setTimeout(resolve, 500));

      // Update profile
      const { error: profileError } = await supabase
        .from('profiles')
        .upsert({
          id: authData.user.id,
          user_id: authData.user.id,
          name: data.name,
          email: data.email,
          username: data.username.toLowerCase(),
          phone: data.phone || null,
          role: roleSlug,
          primary_role_slug: roleSlug,
          membership_level: 'free',
          active_membership_slug: `${roleSlug}_free`,
          organization_name: data.organizationName,
          created_at: new Date().toISOString()
        }, { 
          onConflict: 'user_id',
          ignoreDuplicates: false 
        });

      if (profileError) {
        console.error('Profile error:', profileError);
        throw new Error('Failed to complete registration');
      }

      toast.success('Account created successfully!');
      onSuccess();
    } catch (error: any) {
      console.error('Registration error:', error);
      toast.error(error.message || 'Failed to create account');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 bg-background border-b border-border z-10">
        <div className="flex items-center gap-3 px-4 h-14">
          <button onClick={onBack} className="p-1">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-lg font-semibold">Coach Registration</h1>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 pb-8">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Full Name</Label>
            <Input
              id="name"
              placeholder="Enter full name"
              {...register('name')}
              disabled={isLoading}
            />
            {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter email"
              {...register('email')}
              disabled={isLoading}
            />
            {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="username">Username</Label>
            <Input
              id="username"
              placeholder="Choose username"
              {...register('username')}
              disabled={isLoading}
            />
            {errors.username && <p className="text-sm text-destructive">{errors.username.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter password"
                {...register('password')}
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.password && <p className="text-sm text-destructive">{errors.password.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Phone (Optional)</Label>
            <Input
              id="phone"
              type="tel"
              placeholder="Enter phone number"
              {...register('phone')}
              disabled={isLoading}
            />
          </div>

          <div className="space-y-2">
            <Label>Coach Type</Label>
            <Select onValueChange={(value) => setValue('coachType', value as 'club' | 'college')} disabled={isLoading}>
              <SelectTrigger>
                <SelectValue placeholder="Select coach type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="club">Club Coach</SelectItem>
                <SelectItem value="college">College Coach</SelectItem>
              </SelectContent>
            </Select>
            {errors.coachType && <p className="text-sm text-destructive">{errors.coachType.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="organizationName">
              {coachType === 'college' ? 'College/University' : 'Club/Organization Name'}
            </Label>
            <Input
              id="organizationName"
              placeholder={coachType === 'college' ? 'Enter college name' : 'Enter club name'}
              {...register('organizationName')}
              disabled={isLoading}
            />
            {errors.organizationName && <p className="text-sm text-destructive">{errors.organizationName.message}</p>}
          </div>

          <Button 
            type="submit" 
            className="w-full h-12 mt-6" 
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating Account...
              </>
            ) : (
              'Create Coach Account'
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default MobileCoachRegistration;